import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { shareTargetUrls, copyText } from "./bankRegistryShare";

// Brand hue for the recognizable WhatsApp mark only — logos keep their own
// colour to stay identifiable, so this sits outside the theme palette by intent.
const WHATSAPP_GREEN = "#25D366";

/**
 * In-app share sheet shown when this browser has no native Web Share API
 * (notably Linux desktop Chrome). WhatsApp/Email open that app's own compose
 * screen — never sends; Copy is a share option in the same row, not a
 * separate footer control.
 */
export default function ShareBankDetailsDialog({ shareText, subject, open, onClose, copyLabel, copiedLabel }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open, shareText]);

  if (!shareText) return null;

  const targets = shareTargetUrls({ text: shareText, subject });

  const openTarget = (href) => {
    // Share-intent deep links open the target's compose UI in a new context;
    // the dialog itself is the deliberate interstitial, so no UrlDialog is needed.
    window.open(href, "_blank", "noopener,noreferrer");
    onClose?.();
  };

  const handleCopy = async () => {
    const ok = await copyText(shareText);
    setCopied(ok);
  };

  const titleId = "bank-share-title";
  const channels = [
    {
      key: "whatsapp",
      label: t("WhatsApp"),
      Icon: WhatsAppIcon,
      color: WHATSAPP_GREEN,
      onClick: () => openTarget(targets.whatsapp),
    },
    {
      key: "email",
      label: t("Email"),
      Icon: EmailOutlinedIcon,
      color: "primary.main",
      onClick: () => openTarget(targets.email),
    },
    {
      key: "copy",
      label: copied ? copiedLabel ?? t("bankDetailsCopied") : copyLabel ?? t("copyBankDetails"),
      Icon: copied ? CheckIcon : ContentCopyOutlinedIcon,
      color: copied ? "primary.main" : "neutral.body",
      onClick: handleCopy,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 32px)", sm: 380 },
          maxWidth: "calc(100% - 32px)",
          borderRadius: "16px",
          border: "1.5px solid",
          borderColor: "divider",
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h3" component="span">
          {t("Share")}
        </Typography>
        <IconButton onClick={onClose} aria-label={t("Close")} size="small" sx={{ color: "neutral.body" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", justifyContent: "space-around", gap: 1 }}>
        {channels.map(({ key, label, Icon, color, onClick }) => (
          <Box
            key={key}
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, minWidth: 72 }}
          >
            <IconButton
              onClick={onClick}
              aria-label={`${t("Share")} ${label}`}
              sx={{
                width: 56,
                height: 56,
                border: "1.5px solid",
                borderColor: "divider",
                color,
                "&:hover": { borderColor: color },
              }}
            >
              <Icon />
            </IconButton>
            <Typography variant="caption" sx={{ color: "neutral.headings" }}>
              {label}
            </Typography>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}

ShareBankDetailsDialog.propTypes = {
  /** The plain-text share content; the dialog is inert while empty. */
  shareText: PropTypes.string,
  subject: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  copyLabel: PropTypes.string,
  copiedLabel: PropTypes.string,
};
